import { ButtonGroup, IconButton, Tooltip, useDisclosure } from '@chakra-ui/react';
import {
  ContentTypes,
  ReferenceSourceType,
  UserSentimentType,
} from '@/client/services/api/graphql/gql/graphql';
import { Copy, ThumbsDown, ThumbsUp } from '@/client/components/icons/ContinuIcons';

import AIAgentFeedbackModal from '@/client/components/ai-agent/v2/AIAgentFeedbackModal';
import type { GetConversation } from '@/client/services/api/graphql/gql/graphql';
import { useAddExchangeFeedback } from '@/client/components/ai-agent/v2/hooks/useAddExchangeFeedback';
import { useNavigateByContentType } from '@/client/services/hooks/navigation/useNavigateByContentType';
import { useTranslation } from 'react-i18next';

interface AIAgentFeedbackGroupProps {
  exchange: GetConversation;
}

export default function AIAgentFeedbackGroup({ exchange }: AIAgentFeedbackGroupProps) {
  const { t } = useTranslation();
  const { contentTypeMap } = useNavigateByContentType();
  const { addExchangeFeedback } = useAddExchangeFeedback();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { id: exchangeId, response: exchangeResponse, references: exchangeReferences } = exchange;

  const feedbackOptions = [
    {
      label: t('aiAgent.feedback.copy'),
      icon: <Copy />,
      onClick: () => {
        let clipboardText = exchangeResponse;

        // Add references as links if they exist
        if (exchangeReferences && exchangeReferences.length > 0) {
          clipboardText += '\n\nReferences:\n';

          exchangeReferences.forEach((reference) => {
            const {
              contentType,
              contentId,
              contentTitle,
              sourceType,
              link: sourceLink,
            } = reference;

            // Skip certain content types that don't have public links
            if (
              contentType === ContentTypes.Poll ||
              contentType === ContentTypes.MultiSessionWorkshopSession ||
              contentType === ContentTypes.Presentation ||
              contentType === ContentTypes.Quiz
            ) {
              return;
            }

            if (sourceType === ReferenceSourceType.Url && sourceLink) {
              clipboardText += `• ${sourceLink}\n`;
            }
            if (contentType && contentId && contentTitle) {
              const contentTypeSlug = contentTypeMap[contentType];

              if (contentTypeSlug) {
                const link = `${window.location.origin}/${contentTypeSlug}/${contentId}`;

                clipboardText += `• ${contentTitle}: ${link}\n`;
              }
            }
          });
        }

        navigator.clipboard.writeText(clipboardText);
      },
    },
    {
      label: t('aiAgent.feedback.helpful'),
      icon: <ThumbsUp />,
      onClick: () =>
        addExchangeFeedback.mutateAsync({
          feedbackInput: {
            userFeedback: exchangeResponse,
            userSentiment: UserSentimentType.Positive,
          },
          id: exchangeId,
        }),
    },
    {
      label: t('aiAgent.feedback.notHelpful'),
      icon: <ThumbsDown />,
      onClick: () => onOpen(),
    },
  ];

  return (
    <>
      <ButtonGroup size="xs">
        {feedbackOptions.map((option) => (
          <Tooltip key={option.label} hasArrow label={option.label}>
            <IconButton
              backgroundColor="transparent"
              color="neutral.1500"
              borderRadius="full"
              aria-label={option.label}
              icon={option.icon}
              onClick={() => option.onClick()}
              _hover={{
                backgroundColor: 'neutral.50',
              }}
            />
          </Tooltip>
        ))}
      </ButtonGroup>

      <AIAgentFeedbackModal isOpen={isOpen} onClose={onClose} exchangeId={exchangeId} />
    </>
  );
}
